import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PawPrint, Flame, Heart, Sparkles, ArrowRight } from "lucide-react";
import { Section, Container, Eyebrow, DisplayH1, Reveal } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMOJIS = [
  { key: "paw",   Icon: PawPrint, label: "Paw"   },
  { key: "gold",  Icon: Sparkles, label: "Gold"  },
  { key: "heart", Icon: Heart,    label: "Love"  },
  { key: "fire",  Icon: Flame,    label: "Fire"  },
];

export default function FounderWall() {
  const { user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/founder-qa/wall")
      .then((r) => setQuestions(r.data.questions || []))
      .catch(() => setQuestions([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const react = async (question_id, emoji) => {
    if (!user) { toast.error("Sign in to react."); return; }
    try {
      await api.post(`/founder-qa/${question_id}/react`, { emoji });
      // optimistic update
      setQuestions((qs) =>
        qs.map((q) => {
          if (q.question_id !== question_id) return q;
          const mine = { ...(q.my_reactions || {}) };
          const counts = { ...(q.reactions || {}) };
          if (mine[emoji]) {
            delete mine[emoji];
            counts[emoji] = Math.max(0, (counts[emoji] || 0) - 1);
          } else {
            mine[emoji] = true;
            counts[emoji] = (counts[emoji] || 0) + 1;
          }
          return { ...q, my_reactions: mine, reactions: counts };
        })
      );
    } catch { toast.error("Could not react."); }
  };

  return (
    <>
      <Section testid="wall-hero" className="pt-40 pb-16">
        <Container>
          <Eyebrow>The Founder's Wall</Eyebrow>
          <DisplayH1 className="mt-6 max-w-4xl">Answers from Toriano's desk.</DisplayH1>
          <p className="mt-8 max-w-2xl text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">
            A public archive of the questions Toriano has answered on livestream and in
            written replies. React with the pack — leave a paw, gold, love, or fire.
          </p>
          <div className="mt-6">
            <Link to="/founder-qa" className="btn-ghost">Ask your own question <ArrowRight size={14} /></Link>
          </div>
        </Container>
      </Section>

      <Section testid="wall-list" className="pb-32">
        <Container>
          {loading && <div className="text-[color:var(--wk-bone-dim)]">Loading the wall…</div>}
          {!loading && questions.length === 0 && (
            <div className="card-lux p-10 text-center max-w-2xl mx-auto">
              <Sparkles className="text-[color:var(--wk-gold)] mx-auto" />
              <div className="mt-4 font-serif-display text-2xl">Publishing soon.</div>
              <p className="mt-2 text-[color:var(--wk-bone-dim)]">
                Toriano's first public answers publish after next month's founder livestream.
                Submit yours now to be considered.
              </p>
              <Link to="/founder-qa" className="btn-gold mt-6">Submit a question</Link>
            </div>
          )}
          <div className="grid gap-6 md:grid-cols-2">
            {questions.map((q, i) => (
              <Reveal key={q.question_id} delay={0.04 * i}>
                <motion.article
                  whileHover={{ y: -4 }}
                  className="card-lux p-8 h-full flex flex-col"
                  data-testid={`wall-card-${q.question_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="overline text-[color:var(--wk-gold)]">{q.topic || "General"}</div>
                    <div className="text-xs text-[color:var(--wk-bone-dim)]">
                      {q.answered_at ? new Date(q.answered_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}
                    </div>
                  </div>
                  <div className="mt-4 font-serif-display text-2xl leading-tight">"{q.question}"</div>
                  <div className="mt-2 text-sm text-[color:var(--wk-bone-dim)]">— {q.user_name || "A Wolfkeeper member"}</div>

                  {q.answer && (
                    <div className="mt-6 pt-6 border-t border-white/10 flex-1">
                      <div className="overline text-[10px]">Toriano's reply</div>
                      <p className="mt-3 text-[color:var(--wk-bone)] leading-relaxed whitespace-pre-wrap">
                        {q.answer}
                      </p>
                    </div>
                  )}

                  <div className="mt-6 pt-4 border-t border-white/10 flex flex-wrap items-center gap-2">
                    {EMOJIS.map(({ key, Icon, label }) => {
                      const active = q.my_reactions?.[key];
                      const count = q.reactions?.[key] || 0;
                      return (
                        <button
                          key={key}
                          onClick={() => react(q.question_id, key)}
                          data-testid={`wall-react-${q.question_id}-${key}`}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border transition-colors ${
                            active
                              ? "bg-[color:var(--wk-gold)] text-black border-[color:var(--wk-gold)]"
                              : "border-white/15 text-[color:var(--wk-bone-dim)] hover:border-[color:var(--wk-gold)] hover:text-[color:var(--wk-gold)]"
                          }`}
                          aria-label={label}
                        >
                          <Icon size={12} />
                          <span className="tabular-nums">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </motion.article>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>
    </>
  );
}
